// IQ report developer/design preview panel — NOT production scoring
// logic. Lets product/design preview the report's visual states
// (section bars, overall band/badge, Estimated IQ, Consistency).
// Report-local, disposable by design: delete this dev-tools/ directory
// and its import/render in component.js to remove the feature entirely.
//
// One layer:
//   applyDevSourceInputs — injects a scenario's domain percentages (plus a
//   matching synthetic score — see the function's own comment) onto the
//   RAW assessmentResult, strictly BEFORE mapIQReportModel runs. Every
//   other value (Estimated IQ, bandKey, Consistency, ...) falls out of
//   mapIQReportModel naturally from those two inputs — DevTools never
//   hand-authors a second, competing copy of any calculated value.
//
// Presets are explicit, named QA fixtures only.

import React, { useState } from "react";
import { LOCALE } from "../../../utils/constants";
import { canonicalizeDomainKey } from "../report-model";
import { QUESTIONS_PER_DOMAIN } from "../iq-assessment-config";
import {
  EMPTY_DEV_OVERRIDES,
  NAMED_DEV_PRESETS,
  DEV_PRESET_NAMES,
} from "./report-debug-scenarios";

export { EMPTY_DEV_OVERRIDES, NAMED_DEV_PRESETS, DEV_PRESET_NAMES };

const DOMAIN_LABELS = {
  pattern_recognition: "Pattern Recognition",
  verbal_reasoning: "Verbal Reasoning",
  numerical_reasoning: "Numerical Reasoning",
  logical_deduction: "Logical Deduction",
};

// Dev Controls language switcher options — selecting one performs a full
// navigation to this report's real locale-aware route (see component.js's
// handleDevLocaleChange), not local UI state, so content and i18next both
// follow the normal app localization path afterward.
const LANGUAGE_OPTIONS = [
  { code: LOCALE.EN, label: "English" },
  { code: LOCALE.ES, label: "Spanish" },
  { code: LOCALE.FR, label: "French" },
  { code: LOCALE.JA, label: "Japanese" },
  { code: LOCALE.IT, label: "Italian" },
];

// Environments where the dev panel is allowed to appear at all, and (via
// isNonProdEnv below) where component.js may fall back to DEV FIXTURE DATA
// when the real assessment result is missing. Explicit allowlist rather
// than `!== "PRODUCTION"` — an unknown/missing/misspelled
// REACT_APP_ENVIRONMENT value must fail closed, not accidentally pass.
export const DEV_PREVIEW_ALLOWED_ENVIRONMENTS = ["DEVELOPMENT", "QA", "STAGING"];

// Environment-only check (no query-param requirement) — used by
// component.js to decide whether a missing real result may fall back to
// mock-data.js. Deliberately a strict subset of isDevPreviewAllowed below:
// the DevTools panel additionally requires the explicit env flag and
// ?reportDebug=1 query flag; the mock-data fallback does not.
export const isNonProdEnv = DEV_PREVIEW_ALLOWED_ENVIRONMENTS.includes(
  process.env.REACT_APP_ENVIRONMENT
);

// Gate: requires ALL THREE — an allowlisted lower-environment build, the
// explicit REACT_APP_ENABLE_REPORT_DEV_TOOLS=true flag, AND the
// ?reportDebug=1 query flag. Also reused (see component.js) to decide
// whether a missing real result may fall back to DEV FIXTURE DATA —
// production is never eligible for either behavior.
export const isDevPreviewAllowed = (queryParams) =>
  DEV_PREVIEW_ALLOWED_ENVIRONMENTS.includes(process.env.REACT_APP_ENVIRONMENT) &&
  process.env.REACT_APP_ENABLE_REPORT_DEV_TOOLS === "true" &&
  queryParams.get("reportDebug") === "1";

// Pure function on the RAW assessmentResult, strictly BEFORE
// mapIQReportModel runs. Replaces domain `percentage` (and a matching
// synthetic `score`) for whichever sections a scenario specifies;
// everything else passes through untouched. `domainPercentages`
// null/undefined = no override (Reset).
//
// Synthesizing `score` here (2026-09-17 backend contract update) is a
// deliberate, narrow exception to "never reconstruct score from
// percentage": that rule protects REAL backend userTraits, where score is
// now the authoritative field. Here there is no real score for a
// percentage a QA person just dialed into a slider/preset — the whole
// trait is synthetic preview data — so a matching score is the only way
// to keep "Estimated IQ" tracking the override instead of silently
// freezing on whatever score the underlying mock/real result happened to
// carry.
export const applyDevSourceInputs = (assessmentResult, domainPercentages) => {
  if (!domainPercentages || !assessmentResult) return assessmentResult;
  return {
    ...assessmentResult,
    userTraits: (assessmentResult.userTraits || []).map((userTrait) => {
      const key = canonicalizeDomainKey(userTrait.trait?.title);
      const overridePercentage = domainPercentages[key];
      if (overridePercentage == null) return userTrait;
      return {
        ...userTrait,
        percentage: overridePercentage,
        score: Math.round((overridePercentage / 100) * QUESTIONS_PER_DOMAIN),
      };
    }),
  };
};

const Slider = ({ label, value, onChange }) => (
  <label className="block text-xs font-semibold text-gray-600 mb-3">
    <div className="flex justify-between mb-1">
      <span>{label}</span>
      <span className="text-gray-400">{value ?? "—"}</span>
    </div>
    <input
      type="range"
      min={0}
      max={100}
      value={value ?? 50}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full accent-indigo-600"
    />
  </label>
);

const ReportDebugPanel = ({
  overrides,
  onDomainChange,
  onPreset,
  onReset,
  currentLocale,
  onLocaleChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {isOpen && (
        // Capped to the viewport and split into a flex-shrink-0 header + a
        // min-h-0/overflow-y-auto body, so a panel taller than the screen
        // scrolls internally instead of extending off-screen — the ⚙
        // toggle and header stay reachable at all times on short mobile
        // viewports.
        <div className="mb-3 w-72 max-h-[calc(100vh-100px)] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-5 pt-5 mb-4 flex-shrink-0">
            <h4 className="font-bold text-sm text-gray-800">Dev Controls</h4>
            <span className="text-[10px] font-bold uppercase tracking-wide text-amber-600 bg-amber-50 rounded-full px-2 py-1">
              Preview only
            </span>
          </div>

          <div className="min-h-0 overflow-y-auto overscroll-contain px-5 pb-5">
            <div className="mb-4">
              <div className="text-xs font-semibold text-gray-600 mb-1">Language</div>
              <div className="flex flex-wrap gap-1.5">
                {LANGUAGE_OPTIONS.map((option) => (
                  <button
                    key={option.code}
                    type="button"
                    onClick={() => onLocaleChange(option.code)}
                    className={`text-xs font-semibold rounded-lg px-2.5 py-1 ${
                      currentLocale === option.code
                        ? "bg-indigo-600 text-white"
                        : "bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Overall percentage has no independent slider — it is now
                confirmed-derived (sum of domain raws / 60), never an
                independently settable value. Moving a domain slider below
                recomputes overall (and both bandKeys) through the exact
                same confirmed calculation functions real backend
                percentages use. */}
            {Object.keys(DOMAIN_LABELS).map((key) => (
              <Slider
                key={key}
                label={DOMAIN_LABELS[key]}
                value={overrides.domainPercentages?.[key]}
                onChange={(value) => onDomainChange(key, value)}
              />
            ))}

            <div className="flex gap-2 mt-2 mb-3">
              {DEV_PRESET_NAMES.map((name) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => onPreset(name)}
                  className="flex-1 text-xs font-semibold capitalize bg-indigo-50 text-indigo-700 rounded-lg py-1.5 hover:bg-indigo-100"
                >
                  {name}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={onReset}
              className="w-full text-xs font-semibold text-gray-500 border border-gray-200 rounded-lg py-1.5 hover:bg-gray-50"
            >
              Reset to source result
            </button>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="w-12 h-12 rounded-full bg-indigo-600 text-white shadow-lg flex items-center justify-center text-lg font-bold hover:bg-indigo-700"
        title="Dev Controls"
      >
        {isOpen ? "×" : "⚙"}
      </button>
    </div>
  );
};

export default ReportDebugPanel;
